/**
 * Evolu database initialization for Satsflow Wallet.
 *
 * Creates an Evolu instance with the complete schema and configures it
 * for the web platform using @evolu/react-web dependencies.
 *
 * ## Mnemonic Initialization Paths
 *
 * Evolu generates its own AppOwner (with a random mnemonic) when `createEvolu`
 * is called. Satsflow needs to use a BIP-85-derived child mnemonic (index 0)
 * as the Evolu owner, so there are two possible approaches:
 *
 * ### Path 1: restoreAppOwner (current implementation)
 * After creating the Evolu instance (which auto-generates a random AppOwner),
 * call `evolu.restoreAppOwner(childMnemonic)` to replace the random owner with
 * the BIP-85-derived one. This effectively:
 * 1. Creates Evolu with a throwaway random mnemonic
 * 2. Immediately replaces it with the BIP-85 child mnemonic
 * 3. All subsequent data is encrypted with the BIP-85-derived keys
 *
 * **Tradeoff:** Creates then discards a temporary mnemonic. Not ideal but
 * functional. The temporary data is overwritten on restore.
 *
 * ### Path 2: LocalAuth.register with mnemonic (alternative)
 * Evolu's LocalAuth API accepts an optional `mnemonic` parameter in `register()`.
 * This allows providing the BIP-85 mnemonic at registration time, avoiding the
 * create-then-restore dance. However, LocalAuth is marked as `@experimental`
 * and its API may change.
 *
 * **Current choice: Path 1 (restoreAppOwner)** because it uses the stable,
 * documented API. When LocalAuth stabilizes, we should migrate to Path 2
 * for cleaner initialization.
 *
 * @module db/database
 */

import { createEvolu, type Evolu, Mnemonic } from '@evolu/common';
import { evoluReactWebDeps } from '@evolu/react-web';
import { Schema } from './schema';

/** The Evolu schema type for Satsflow Wallet. */
export type SatsflowSchema = typeof Schema;

/** Options for creating the Evolu instance. */
export interface CreateDatabaseOptions {
  /**
   * Instance name for Evolu. Allows multiple Evolu instances
   * to coexist (e.g., for testing). Default: undefined (uses Evolu default).
   */
  readonly name?: string;
}

/**
 * Create and return the Evolu database instance.
 *
 * This creates an Evolu instance with the full Satsflow schema.
 * Evolu auto-generates an AppOwner on creation. To use a BIP-85-derived
 * mnemonic, call `restoreWithMnemonic` after creation.
 *
 * @param options - Optional configuration for the Evolu instance
 * @returns The Evolu instance with the Satsflow schema
 */
export function createDatabase(options?: CreateDatabaseOptions): Evolu<SatsflowSchema> {
  return createEvolu(evoluReactWebDeps)(Schema, {
    ...(options?.name ? { name: options.name } : {}),
  } as Parameters<ReturnType<typeof createEvolu>>[1]);
}

/**
 * Restore the Evolu instance to use a BIP-85-derived child mnemonic.
 *
 * This replaces the auto-generated AppOwner with one derived from the
 * provided mnemonic. Should be called during wallet creation/restore flow.
 *
 * SECURITY: The mnemonic parameter is sensitive. Do not log it.
 *
 * @param evolu - The Evolu instance to restore
 * @param childMnemonic - BIP-85 child mnemonic (index 0) as a plain string
 * @returns Promise that resolves when restore is complete
 * @throws Error if the mnemonic is not a valid BIP-39 mnemonic
 */
export async function restoreWithMnemonic(
  evolu: Evolu<SatsflowSchema>,
  childMnemonic: string,
): Promise<void> {
  const mnemonicResult = Mnemonic.from(childMnemonic);

  if (!mnemonicResult.ok) {
    throw new Error('Invalid mnemonic for Evolu initialization');
  }

  await evolu.restoreAppOwner(mnemonicResult.value, { reload: false });
}
